import type { Widget } from './schemas'

const COLS = 120

export interface GroupDragState { driverId: string; relPos: Map<string, { dx: number; dy: number }> }
type Item = { i: string; x: number; y: number; w: number; h: number }

/**
 * Déplacement groupé : quand un widget sélectionné (le « driver ») bouge, les autres
 * sélectionnés (« followers ») suivent avec le même delta. Fonction pure — porte la logique
 * de Metrix (driver/followers via positions relatives), sans état React.
 * @param ws widgets AVANT ce frame ; @param next layout RGL du frame courant ;
 * @param sel ids sélectionnés ; @param state état group-drag courant (null au 1er frame) ;
 * @param prevPositions positions connues au frame précédent (mutée pour le suivi).
 * @returns { updated, state } — les widgets mis à jour + le nouvel état group-drag.
 */
export function computeGroupMove(
  ws: Widget[],
  next: Item[],
  sel: Set<string>,
  state: GroupDragState | null,
  prevPositions: Map<string, { x: number; y: number }>,
): { updated: Widget[]; state: GroupDragState | null } {
  // seed prevPositions pour les nouveaux items
  for (const item of next) {
    if (!prevPositions.has(item.i)) {
      const orig = ws.find((w) => w.id === item.i)
      prevPositions.set(item.i, orig ? { x: orig.x, y: orig.y } : { x: item.x, y: item.y })
    }
  }

  let groupDrag = state
  if (!groupDrag) {
    for (const item of next) {
      if (!sel.has(item.i)) continue
      const prev = prevPositions.get(item.i)
      if (!prev) continue
      const posChanged = item.x !== prev.x || item.y !== prev.y
      const orig = ws.find((w) => w.id === item.i)
      if (posChanged && orig && item.w === orig.w && item.h === orig.h) {
        const relPos = new Map<string, { dx: number; dy: number }>()
        for (const id of sel) {
          if (id === item.i) continue
          const follower = ws.find((w) => w.id === id)
          if (follower) relPos.set(id, { dx: follower.x - orig.x, dy: follower.y - orig.y })
        }
        groupDrag = { driverId: item.i, relPos }
        break
      }
    }
  }

  let updated: Widget[]
  if (groupDrag) {
    const { driverId, relPos } = groupDrag
    const driverItem = next.find((l) => l.i === driverId)
    updated = ws.map((w) => {
      const found = next.find((l) => l.i === w.id)
      if (!found) return w
      if (w.id === driverId) return { ...w, x: found.x, y: found.y, w: found.w, h: found.h }
      const rel = relPos.get(w.id)
      if (rel && driverItem) {
        return { ...w, x: Math.max(0, Math.min(COLS - w.w, driverItem.x + rel.dx)), y: Math.max(0, driverItem.y + rel.dy) }
      }
      return { ...w, x: found.x, y: found.y, w: found.w, h: found.h }
    })
  } else {
    updated = ws.map((w) => {
      const found = next.find((l) => l.i === w.id)
      return found ? { ...w, x: found.x, y: found.y, w: found.w, h: found.h } : w
    })
  }

  // sync prevPositions
  updated.forEach((w) => prevPositions.set(w.id, { x: w.x, y: w.y }))
  for (const id of [...prevPositions.keys()]) {
    if (!updated.find((w) => w.id === id)) prevPositions.delete(id)
  }

  return { updated, state: groupDrag }
}
